import { useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, CircleX, Info, Minus, PauseCircle, Plus } from "lucide-react";
import { Button, Dialog, Input } from "@akuna/ui";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { OptionTile } from "@/components/packages/plan-card/OptionTile";
import { FutureReservationsScopeField } from "@/components/packages/FutureReservationsScopeField";
import { useLinkedFutureDaysCount } from "@/hooks/useLinkedFutureDaysCount";
import { getBrazilDate } from "@/lib/date-utils";
import { useStayPlans } from "@/hooks/useStayPlans";
import { FixedWeekdaysField } from "@/pages/daycare-hotel/plans/FixedWeekdaysField";
import { computeSwitchProration } from "@/lib/daycare-hotel/planProration";
import { fixedDayDatesInRange } from "@/lib/daycare-hotel/planFixedDays";
import { getBillingCycle } from "@/lib/daycare-hotel/stayPlans";
import { useStayDailyRates } from "@/hooks/useStayDailyRates";
import {
  useSetStayPlanPaused,
  useSwitchStayPlan,
  useUpdateStayPlanTerms,
  type FutureReservationsScope,
} from "@/hooks/useStayPlanContractConfig";
import { useCancelStayPlan } from "@/pages/daycare-hotel/plans/useCancelStayPlan";
import type { StayPlanContract } from "@/hooks/useStayPlanContracts";

export interface StayPlanConfigDialogProps {
  contract: StayPlanContract;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Qual seção destacar ao abrir; "billing" vem do "Alterar cobrança" no card. */
  initialSection?: "terms" | "billing";
}

function formatCurrency(value: number, language: string) {
  return new Intl.NumberFormat(language.startsWith("pt") ? "pt-BR" : "en-US", {
    style: "currency",
    currency: "BRL",
  }).format(value);
}

/** Seção do design: um cartão por assunto, com título em display. */
function ConfigSection({
  title,
  hint,
  highlighted = false,
  children,
}: {
  title: ReactNode;
  hint?: ReactNode;
  highlighted?: boolean;
  children: ReactNode;
}) {
  return (
    <section
      className={`rounded-lg border bg-cream-50 p-4 ${
        highlighted ? "border-primary ring-2 ring-ring ring-offset-2" : "border-border"
      }`}
    >
      <h3 className="font-display text-sm font-bold text-foreground">{title}</h3>
      {hint ? <p className="mt-1.5 text-xs text-muted-foreground">{hint}</p> : null}
      <div className="mt-3">{children}</div>
    </section>
  );
}

export function StayPlanConfigDialog({ contract, open, onOpenChange, initialSection = "terms" }: StayPlanConfigDialogProps) {
  const { t, i18n } = useTranslation("patients");
  const plans = useStayPlans(contract.module);
  const rates = useStayDailyRates();
  const updateTerms = useUpdateStayPlanTerms();
  const setPlanPaused = useSetStayPlanPaused();
  const switchPlan = useSwitchStayPlan();
  const cancelPlan = useCancelStayPlan();

  const [usageLimit, setUsageLimit] = useState(contract.usageLimit ?? 0);
  const [priceCents, setPriceCents] = useState(() => Math.round(contract.price * 100));
  const priceInputRef = useRef<HTMLInputElement | null>(null);
  const [carryoverEnabled, setCarryoverEnabled] = useState(contract.carryoverEnabled);
  const [billingTiming, setBillingTiming] = useState<"start" | "end">(contract.billingTiming);
  const [paused, setPaused] = useState(contract.status === "paused");
  const [newPlanId, setNewPlanId] = useState<string>("");
  const [switchOpen, setSwitchOpen] = useState(false);
  const [switchWeekdays, setSwitchWeekdays] = useState<number[]>(contract.fixedWeekdays);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [pauseScopeOpen, setPauseScopeOpen] = useState(false);
  const [futureScope, setFutureScope] = useState<FutureReservationsScope>("plan");

  // Avulsas que consomem o plano: só quando existem vale perguntar o escopo.
  const linkedDays = useLinkedFutureDaysCount(
    contract.id,
    open && contract.module === "daycare" && contract.status === "active",
  );
  const linkedCount = linkedDays.data ?? 0;
  const askScope = contract.module === "daycare" && linkedCount > 0;

  const isPaused = paused;
  const isRecurring = contract.kind === "recurring";
  const extraRate =
    contract.module === "daycare" ? rates.data?.daycareDailyRate ?? null : rates.data?.hotelNightlyRate ?? null;
  const catalog = (plans.data ?? []).filter((plan) => plan.isActive && plan.id !== contract.planId);
  const targetPlan = catalog.find((plan) => plan.id === newPlanId) ?? null;
  // Trocar para outra matrícula exige redesenhar os dias: o plano novo pode ter
  // uma quantidade diferente da atual.
  const switchNeedsDays = contract.module === "daycare" && targetPlan?.usageRule === "per_week";
  const switchRequiredDays = switchNeedsDays ? targetPlan?.usageLimit ?? 0 : 0;
  const switchDaysReady = !switchNeedsDays || switchWeekdays.length === switchRequiredDays;
  // Mesma conta de `switch_daycare_plan_contract`, para o atendente ver o
  // acerto antes de confirmar em vez de descobrir depois na comanda.
  const prorationPreview = (() => {
    if (contract.usageRule !== "per_week" || !contract.firstDueOn) return null;
    const today = getBrazilDate();
    const cycle = getBillingCycle(contract.firstDueOn, today);
    const occurrences = fixedDayDatesInRange({
      from: cycle.start,
      to: cycle.end,
      weekdays: contract.fixedWeekdays,
    }).length;
    const usedUnits = contract.usages.filter(
      (usage) => usage.source === "checkin" && usage.usedOn >= cycle.start && usage.usedOn <= cycle.end,
    ).length;
    const chargePaid = contract.charges.some(
      (charge) => charge.periodStartsOn === cycle.start && charge.saleStatus === "PAGA",
    );
    return computeSwitchProration({ price: contract.price, cycleOccurrences: occurrences, usedUnits, chargePaid });
  })();

  const save = async (scope: FutureReservationsScope = "plan") => {
    const price = priceCents / 100;
    await updateTerms.mutateAsync({
      module: contract.module,
      contractId: contract.id,
      usageLimit: isRecurring && usageLimit > 0 ? usageLimit : null,
      price: price > 0 ? price : null,
      carryoverEnabled,
      billingTiming,
    });
    if (paused !== (contract.status === "paused")) {
      await setPlanPaused.mutateAsync({ module: contract.module, contractId: contract.id, paused, futureScope: scope });
    }
    onOpenChange(false);
  };

  /** Pausar solta as reservas futuras: com avulsas ligadas, o escopo é escolhido antes. */
  const requestSave = () => {
    const pausingNow = paused && contract.status !== "paused";
    if (pausingNow && askScope) {
      setFutureScope("plan");
      setPauseScopeOpen(true);
      return;
    }
    void save();
  };

  const applyPriceCents = (nextCents: number) => setPriceCents(Math.max(0, Math.min(nextCents, 999999999)));
  const setCaretFromDigitsRight = (digitsRight: number) => requestAnimationFrame(() => {
    const input = priceInputRef.current;
    if (!input) return;
    if (digitsRight <= 0) return input.setSelectionRange(input.value.length, input.value.length);
    let counted = 0;
    let position = input.value.length;
    for (let index = input.value.length - 1; index >= 0; index -= 1) {
      if (/\d/.test(input.value[index])) counted += 1;
      if (counted === digitsRight) { position = index; break; }
    }
    input.setSelectionRange(position, position);
  });
  const handlePriceKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const raw = input.value;
    const start = input.selectionStart ?? raw.length;
    const end = input.selectionEnd ?? start;
    if (event.ctrlKey || event.metaKey || event.altKey || ["Tab", "Enter", "Escape", "Home", "End"].includes(event.key) || event.key.startsWith("Arrow")) return;
    if (event.key === "Backspace" || event.key === "Delete") {
      event.preventDefault();
      if (end > start) { applyPriceCents(0); setCaretFromDigitsRight(0); return; }
      let index = event.key === "Backspace" ? start - 1 : start;
      const step = event.key === "Backspace" ? -1 : 1;
      while (index >= 0 && index < raw.length && !/\d/.test(raw[index])) index += step;
      if (index < 0 || index >= raw.length) return;
      const right = raw.slice(index + (event.key === "Backspace" ? 1 : 0)).replace(/\D/g, "").length;
      const cents = String(priceCents).padStart(3, "0");
      const digit = cents.length - 1 - right;
      applyPriceCents(Number(cents.slice(0, digit) + cents.slice(digit + 1) || 0));
      setCaretFromDigitsRight(right);
      return;
    }
    if (event.key.length === 1 && /\d/.test(event.key)) {
      event.preventDefault();
      if (end > start) { applyPriceCents(Number(event.key)); setCaretFromDigitsRight(0); return; }
      const right = raw.slice(start).replace(/\D/g, "").length;
      const cents = String(priceCents).padStart(3, "0");
      const at = cents.length - right;
      applyPriceCents(Number(cents.slice(0, at) + event.key + cents.slice(at)));
      setCaretFromDigitsRight(right);
      return;
    }
    if (event.key.length === 1) event.preventDefault();
  };

  const doSwitch = async () => {
    if (!newPlanId) return;
    await switchPlan.mutateAsync({
      module: contract.module,
      contractId: contract.id,
      newPlanId,
      usageRule: contract.usageRule,
      fixedWeekdays: switchWeekdays,
    });
    setSwitchOpen(false);
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      mode="adaptive"
      className="sm:max-w-[560px]"
      title={t("stayPlanConfig.title")}
      header={t("stayPlanConfig.subtitle", { plan: contract.planName })}
      closeLabel={t("stayPlanConfig.close")}
      footer={
        <div className="flex w-full items-center gap-3">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t("stayPlanConfig.close")}
          </Button>
          <Button type="button" className="ms-auto" disabled={updateTerms.isPending || setPlanPaused.isPending} onClick={requestSave}>
            {t("stayPlanConfig.save")}
          </Button>
        </div>
      }
    >
      <div className="grid gap-4">
        <ConfigSection title={t("stayPlanConfig.termsTitle")} hint={t("stayPlanConfig.contractOnly")}>
          <div className="grid gap-3.5">
            {isRecurring ? (
              <div className="flex flex-wrap items-center gap-3">
                <Label htmlFor="config-usage-limit" className="text-[0.8125rem] font-normal text-color-medium">
                  {t("stayPlanConfig.usageLimit")}
                </Label>
                <div className="ms-auto flex items-center gap-2 rounded-md border-[1.5px] border-border bg-background p-1">
                  <button
                    type="button"
                    aria-label={t("stayPlanAdjustments.decrease")}
                    disabled={usageLimit <= 1}
                    onClick={() => setUsageLimit((value) => Math.max(1, value - 1))}
                    className="flex h-7 w-7 items-center justify-center rounded-sm border border-border text-color-medium transition-colors hover:bg-foreground/[0.05] disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <Minus className="h-3.5 w-3.5" aria-hidden />
                  </button>
                  <Input
                    id="config-usage-limit"
                    type="number"
                    min={1}
                    value={usageLimit}
                    onChange={(event) => setUsageLimit(Math.max(0, Number(event.target.value) || 0))}
                    className="w-14 border-0 bg-transparent px-0 text-center font-display font-extrabold shadow-none focus-visible:ring-0"
                  />
                  <button
                    type="button"
                    aria-label={t("stayPlanAdjustments.increase")}
                    onClick={() => setUsageLimit((value) => value + 1)}
                    className="flex h-7 w-7 items-center justify-center rounded-sm border border-border text-color-medium transition-colors hover:bg-foreground/[0.05] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <Plus className="h-3.5 w-3.5" aria-hidden />
                  </button>
                </div>
              </div>
            ) : null}

            <div className="flex flex-wrap items-center gap-3">
              <Label htmlFor="config-price" className="text-[0.8125rem] font-normal text-color-medium">
                {t("stayPlanConfig.price")}
              </Label>
              <div className="ms-auto flex items-baseline gap-2">
                <span className="font-display text-md font-bold text-foreground">R$</span>
                <Input
                  ref={priceInputRef}
                  id="config-price"
                  inputMode="numeric"
                  value={priceCents > 0 ? (priceCents / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ""}
                  onKeyDown={handlePriceKeyDown}
                  onChange={() => {}}
                  placeholder="0,00"
                  className="h-10 w-36 font-display text-lg font-bold"
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <p className="text-xs text-muted-foreground">
                {t("stayPlanConfig.extraDayNote", {
                  amount: extraRate ? formatCurrency(extraRate, i18n.language) : t("stayPlanConfig.extraDayUnset"),
                })}
              </p>
            </div>
          </div>
        </ConfigSection>

        {isRecurring ? (
          <ConfigSection title={t("stayPlanConfig.carryoverTitle")} hint={t("stayPlanConfig.carryoverHint")}>
            <div className="grid gap-2.5 sm:grid-cols-2">
              <OptionTile
                name="config-carryover"
                value="expire"
                checked={!carryoverEnabled}
                onSelect={() => setCarryoverEnabled(false)}
              >
                <span className="block text-[0.8125rem] font-semibold text-foreground">
                  {t("stayPlanConfig.expire")}
                </span>
                <span className="mt-1 block text-xs text-muted-foreground">{t("stayPlanConfig.expireHint")}</span>
              </OptionTile>
              <OptionTile
                name="config-carryover"
                value="accumulate"
                checked={carryoverEnabled}
                onSelect={() => setCarryoverEnabled(true)}
              >
                <span className="block text-[0.8125rem] font-semibold text-foreground">
                  {t("stayPlanConfig.accumulate")}
                </span>
                <span className="mt-1 block text-xs text-muted-foreground">{t("stayPlanConfig.accumulateHint")}</span>
              </OptionTile>
            </div>
          </ConfigSection>
        ) : null}

        <ConfigSection title={t("stayPlanConfig.billingTitle")} highlighted={initialSection === "billing"}>
          <div className="grid gap-2.5 sm:grid-cols-2">
            <OptionTile
              name="config-billing"
              value="start"
              checked={billingTiming === "start"}
              onSelect={() => setBillingTiming("start")}
            >
              <span className="block text-[0.8125rem] font-semibold text-foreground">
                {t("stayPlanConfig.billingStart")}
              </span>
              <span className="mt-1 block text-xs text-muted-foreground">{t("stayPlanConfig.billingStartHint")}</span>
            </OptionTile>
            <OptionTile
              name="config-billing"
              value="end"
              checked={billingTiming === "end"}
              onSelect={() => setBillingTiming("end")}
            >
              <span className="block text-[0.8125rem] font-semibold text-foreground">
                {t("stayPlanConfig.billingEnd")}
              </span>
              <span className="mt-1 block text-xs text-muted-foreground">{t("stayPlanConfig.billingEndHint")}</span>
            </OptionTile>
          </div>
          <p className="mt-3 flex items-start gap-2 rounded-md border border-amber-200 bg-state-warning-soft px-3.5 py-2.5 text-xs text-state-warning-foreground">
            <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            {t("stayPlanConfig.billingNextCycle")}
          </p>
        </ConfigSection>

        <ConfigSection title={t("stayPlanConfig.switchTitle")}>
          <div className="flex flex-wrap items-center gap-2.5">
            <span className="rounded-md bg-cream-100 px-3 py-2 text-[0.8125rem] font-semibold text-color-medium">
              {contract.planName}
            </span>
            <ArrowRight className="h-4 w-4 shrink-0 text-color-light" aria-hidden />
            <Select value={newPlanId} onValueChange={setNewPlanId}>
              <SelectTrigger className="min-w-[12rem] flex-1" aria-label={t("stayPlanConfig.switchPlaceholder")}>
                <SelectValue placeholder={t("stayPlanConfig.switchPlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                {catalog.map((plan) => (
                  <SelectItem key={plan.id} value={plan.id}>
                    {plan.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              variant="outline"
              disabled={!newPlanId || !switchDaysReady}
              onClick={() => setSwitchOpen(true)}
            >
              {t("stayPlanConfig.switchAction")}
            </Button>
          </div>
          {switchNeedsDays ? (
            <div className="mt-3">
              <FixedWeekdaysField
                value={switchWeekdays}
                required={switchRequiredDays}
                onChange={setSwitchWeekdays}
                disabled={switchPlan.isPending}
              />
            </div>
          ) : null}
          {prorationPreview && prorationPreview.mode !== "none" ? (
            <p role="status" className="mt-2.5 text-xs text-muted-foreground">
              {t(`planCard.proration.${prorationPreview.mode}`, {
                amount: formatCurrency(Math.abs(prorationPreview.amount), i18n.language),
              })}
            </p>
          ) : null}
          <p className="mt-2.5 text-xs text-muted-foreground">{t("stayPlanConfig.switchHint")}</p>
        </ConfigSection>

        <ConfigSection title={t("stayPlanConfig.statusTitle")}>
          <div className="grid gap-2.5">
            <button
              type="button"
              disabled={setPlanPaused.isPending || updateTerms.isPending}
              aria-labelledby="config-pause-label"
              aria-describedby="config-pause-hint"
              onClick={() => setPaused(!isPaused)}
              className="flex w-full items-start gap-2.5 rounded-md border-[1.5px] border-border bg-background p-3.5 text-start transition-colors hover:bg-foreground/[0.03] disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <PauseCircle className="mt-0.5 h-[18px] w-[18px] shrink-0 text-state-warning-foreground" aria-hidden />
              <span>
                <span id="config-pause-label" className="block text-[0.8125rem] font-semibold text-foreground">
                  {isPaused ? t("stayPlanConfig.resume") : t("stayPlanConfig.pause")}
                </span>
                <span id="config-pause-hint" className="mt-0.5 block text-xs text-muted-foreground">
                  {isPaused ? t("stayPlanConfig.resumeHint") : t("stayPlanConfig.pauseHint")}
                </span>
              </span>
            </button>

            <button
              type="button"
              onClick={() => {
                setFutureScope("plan");
                setCancelOpen(true);
              }}
              aria-labelledby="config-cancel-label"
              aria-describedby="config-cancel-hint"
              className="flex w-full items-start gap-2.5 rounded-md border-[1.5px] border-state-danger/30 bg-background p-3.5 text-start transition-colors hover:bg-state-danger-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <CircleX className="mt-0.5 h-[18px] w-[18px] shrink-0 text-state-danger" aria-hidden />
              <span>
                <span id="config-cancel-label" className="block text-[0.8125rem] font-semibold text-state-danger-foreground">
                  {t("stayPlanConfig.cancelPlan")}
                </span>
                <span id="config-cancel-hint" className="mt-0.5 block text-xs text-state-danger-foreground/80">
                  {t("stayPlanConfig.cancelPlanHint")}
                </span>
              </span>
            </button>
          </div>
        </ConfigSection>
      </div>

      <AlertDialog open={switchOpen} onOpenChange={setSwitchOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("stayPlanConfig.switchConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("stayPlanConfig.switchConfirmBody")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("stayPlanConfig.back")}</AlertDialogCancel>
            <AlertDialogAction disabled={switchPlan.isPending} onClick={() => void doSwitch()}>
              {t("stayPlanConfig.switchConfirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("stayPlans.cancelTitle", { name: contract.planName })}</AlertDialogTitle>
            <AlertDialogDescription>{t("stayPlans.cancelDescription")}</AlertDialogDescription>
          </AlertDialogHeader>
          {askScope ? (
            <FutureReservationsScopeField value={futureScope} onChange={setFutureScope} linkedCount={linkedCount} />
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel>{t("stayPlans.cancelBack")}</AlertDialogCancel>
            <AlertDialogAction
              disabled={cancelPlan.isPending}
              onClick={async () => {
                await cancelPlan.mutateAsync({ module: contract.module, contractId: contract.id, futureScope });
                onOpenChange(false);
              }}
            >
              {t("stayPlans.cancelConfirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={pauseScopeOpen} onOpenChange={setPauseScopeOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("stayPlanConfig.futureScope.pauseTitle", { name: contract.planName })}</AlertDialogTitle>
            <AlertDialogDescription>{t("stayPlanConfig.futureScope.pauseDescription")}</AlertDialogDescription>
          </AlertDialogHeader>
          <FutureReservationsScopeField value={futureScope} onChange={setFutureScope} linkedCount={linkedCount} />
          <AlertDialogFooter>
            <AlertDialogCancel>{t("stayPlanConfig.back")}</AlertDialogCancel>
            <AlertDialogAction
              disabled={setPlanPaused.isPending || updateTerms.isPending}
              onClick={() => void save(futureScope)}
            >
              {t("stayPlanConfig.futureScope.confirmPause")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
